/**
 * Ludo Game Engine
 *
 * Core game logic: board initialization, dice rolling, token movement,
 * capture detection, safe spot enforcement, home stretch logic,
 * turn management (extra turns on 6, triple-six forfeit),
 * win condition detection, and serialization.
 */

import {
  PlayerColor,
  PlayerType,
  TokenLocation,
  TokenPosition,
  TokenInfo,
  TokenIdentifier,
  LudoMove,
  MoveAction,
  MoveValidation,
  MoveResult,
  MoveRecord,
  LudoGameState,
  LudoGameStatus,
  LudoGameSettings,
  PLAYER_COLORS,
  PLAYER_START_POSITIONS,
  HOME_STRETCH_ENTRY,
  SAFE_SPOTS,
  TRACK_LENGTH,
  HOME_STRETCH_LENGTH,
  MAX_CONSECUTIVE_SIXES,
  TOKENS_PER_PLAYER,
} from './types';

export class LudoEngine {
  private state: LudoGameState;

  constructor() {
    this.state = this.createEmptyState();
  }

  // =========================================================================
  // Initialization
  // =========================================================================

  initializeGame(
    playerCount: number,
    playerTypes: PlayerType[],
    settings?: Partial<LudoGameSettings>
  ): void {
    const count = Math.max(2, Math.min(4, playerCount));
    const activePlayers = PLAYER_COLORS.slice(0, count);

    const tokens: Record<PlayerColor, TokenPosition[]> = {
      red: [], blue: [], green: [], yellow: [],
    };
    const playerTypesMap: Record<PlayerColor, PlayerType> = {
      red: 'human', blue: 'human', green: 'human', yellow: 'human',
    };
    const tokensHome: Record<PlayerColor, number> = {
      red: 0, blue: 0, green: 0, yellow: 0,
    };

    for (let i = 0; i < count; i++) {
      const color = activePlayers[i];
      playerTypesMap[color] = playerTypes[i] || 'human';
      tokens[color] = [];
      for (let t = 0; t < TOKENS_PER_PLAYER; t++) {
        tokens[color].push({
          tokenIndex: t,
          location: 'home_base',
        });
      }
    }

    this.state = {
      tokens,
      playerCount: count,
      activePlayers,
      playerTypes: playerTypesMap,
      currentTurn: activePlayers[0],
      consecutiveSixes: 0,
      lastDiceValue: null,
      awaitingMove: false,
      tokensHome,
      moveHistory: [],
      gameStatus: { type: 'active' },
      gameId: `ludo-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
      startedAt: Date.now(),
      lastMoveAt: Date.now(),
    };
  }

  // =========================================================================
  // Dice
  // =========================================================================

  rollDice(): number {
    const value = Math.floor(Math.random() * 6) + 1;
    this.state.lastDiceValue = value;
    this.state.awaitingMove = true;
    return value;
  }

  setDiceValue(value: number): void {
    const clamped = Math.max(1, Math.min(6, Math.round(value)));
    this.state.lastDiceValue = clamped;
    this.state.awaitingMove = true;
  }

  // =========================================================================
  // Move Validation
  // =========================================================================

  getValidMoves(player: PlayerColor): LudoMove[] {
    if (this.state.gameStatus.type !== 'active') return [];
    if (this.state.currentTurn !== player) return [];
    if (this.state.lastDiceValue === null) return [];

    const diceValue = this.state.lastDiceValue;
    const moves: LudoMove[] = [];
    const playerTokens = this.state.tokens[player];

    for (let i = 0; i < playerTokens.length; i++) {
      const token = playerTokens[i];

      if (token.location === 'home') continue;

      if (token.location === 'home_base') {
        if (diceValue === 6) {
          const startPos = PLAYER_START_POSITIONS[player];
          // Check if own token is already on start position
          if (!this.isOwnTokenAt(player, 'track', startPos)) {
            moves.push({
              player,
              tokenIndex: i,
              action: 'enter',
              diceValue,
            });
          }
        }
        continue;
      }

      if (token.location === 'track') {
        const result = this.calculateTrackMove(player, token.trackPosition!, diceValue);
        if (result) {
          const { newLocation, newTrackPos, newHomeStretchPos } = result;
          // Check if destination is blocked by own token
          if (newLocation === 'track' && this.isOwnTokenAt(player, 'track', newTrackPos!)) {
            continue;
          }
          if (newLocation === 'home_stretch' && this.isOwnTokenAt(player, 'home_stretch', newHomeStretchPos!)) {
            continue;
          }
          moves.push({
            player,
            tokenIndex: i,
            action: 'move',
            diceValue,
          });
        }
        continue;
      }

      if (token.location === 'home_stretch') {
        const newPos = token.homeStretchPosition! + diceValue;
        if (newPos === HOME_STRETCH_LENGTH) {
          // Exact roll to home
          moves.push({
            player,
            tokenIndex: i,
            action: 'move',
            diceValue,
          });
        } else if (newPos < HOME_STRETCH_LENGTH) {
          // Move within home stretch
          if (!this.isOwnTokenAt(player, 'home_stretch', newPos)) {
            moves.push({
              player,
              tokenIndex: i,
              action: 'move',
              diceValue,
            });
          }
        }
        // If newPos > HOME_STRETCH_LENGTH, overshoots — no valid move
        continue;
      }
    }

    return moves;
  }

  isValidMove(player: PlayerColor, move: LudoMove): MoveValidation {
    if (this.state.gameStatus.type !== 'active') {
      return { valid: false, reason: 'game_over' };
    }
    if (this.state.currentTurn !== player) {
      return { valid: false, reason: 'not_your_turn' };
    }
    if (move.tokenIndex < 0 || move.tokenIndex >= TOKENS_PER_PLAYER) {
      return { valid: false, reason: 'invalid_token' };
    }

    const token = this.state.tokens[player][move.tokenIndex];
    if (!token) {
      return { valid: false, reason: 'invalid_token' };
    }
    if (token.location === 'home') {
      return { valid: false, reason: 'token_already_home' };
    }

    const validMoves = this.getValidMoves(player);
    const isInValidMoves = validMoves.some(
      (m) => m.tokenIndex === move.tokenIndex && m.action === move.action
    );

    if (!isInValidMoves) {
      if (token.location === 'home_base' && move.action === 'enter' && move.diceValue !== 6) {
        return { valid: false, reason: 'no_six_to_enter' };
      }
      if (move.action === 'move') {
        // Check specific reasons
        if (token.location === 'home_stretch') {
          const newPos = token.homeStretchPosition! + move.diceValue;
          if (newPos > HOME_STRETCH_LENGTH) {
            return { valid: false, reason: 'exceeds_home' };
          }
        }
        // Check for own token blocking
        if (token.location === 'track') {
          const result = this.calculateTrackMove(player, token.trackPosition!, move.diceValue);
          if (result && result.newLocation === 'track' && this.isOwnTokenAt(player, 'track', result.newTrackPos!)) {
            return { valid: false, reason: 'blocked_by_own_token' };
          }
        }
      }
      return { valid: false, reason: 'invalid_token' };
    }

    return { valid: true };
  }

  // =========================================================================
  // Move Execution
  // =========================================================================

  executeMove(player: PlayerColor, move: LudoMove): MoveResult {
    const validation = this.isValidMove(player, move);
    if (!validation.valid) {
      return {
        success: false,
        move,
        newPosition: this.state.tokens[player][move.tokenIndex],
        reachedHome: false,
        extraTurn: false,
        tripleSix: false,
        turnSkipped: false,
        gameOver: false,
        error: validation.reason,
      };
    }

    const token = this.state.tokens[player][move.tokenIndex];
    const fromPosition: TokenPosition = { ...token };
    let captured: TokenInfo | undefined;
    let reachedHome = false;

    if (move.action === 'enter') {
      // Enter board at starting square
      const startPos = PLAYER_START_POSITIONS[player];
      // Check for capture at start position
      captured = this.checkAndExecuteCapture(player, startPos);

      this.state.tokens[player][move.tokenIndex] = {
        tokenIndex: move.tokenIndex,
        location: 'track',
        trackPosition: startPos,
      };
    } else if (move.action === 'move') {
      if (token.location === 'track') {
        const result = this.calculateTrackMove(player, token.trackPosition!, move.diceValue)!;

        if (result.newLocation === 'track') {
          // Check for capture
          captured = this.checkAndExecuteCapture(player, result.newTrackPos!);
          this.state.tokens[player][move.tokenIndex] = {
            tokenIndex: move.tokenIndex,
            location: 'track',
            trackPosition: result.newTrackPos!,
          };
        } else if (result.newLocation === 'home_stretch') {
          this.state.tokens[player][move.tokenIndex] = {
            tokenIndex: move.tokenIndex,
            location: 'home_stretch',
            homeStretchPosition: result.newHomeStretchPos!,
          };
        } else if (result.newLocation === 'home') {
          this.state.tokens[player][move.tokenIndex] = {
            tokenIndex: move.tokenIndex,
            location: 'home',
          };
          this.state.tokensHome[player]++;
          reachedHome = true;
        }
      } else if (token.location === 'home_stretch') {
        const newPos = token.homeStretchPosition! + move.diceValue;
        if (newPos === HOME_STRETCH_LENGTH) {
          this.state.tokens[player][move.tokenIndex] = {
            tokenIndex: move.tokenIndex,
            location: 'home',
          };
          this.state.tokensHome[player]++;
          reachedHome = true;
        } else {
          this.state.tokens[player][move.tokenIndex] = {
            tokenIndex: move.tokenIndex,
            location: 'home_stretch',
            homeStretchPosition: newPos,
          };
        }
      }
    }

    // Determine extra turn and triple-six
    const isExtraTurn = move.diceValue === 6;
    let tripleSix = false;

    if (isExtraTurn) {
      this.state.consecutiveSixes++;
      if (this.state.consecutiveSixes >= MAX_CONSECUTIVE_SIXES) {
        tripleSix = true;
      }
    } else {
      this.state.consecutiveSixes = 0;
    }

    // Check win condition
    const gameOver = this.state.tokensHome[player] === TOKENS_PER_PLAYER;
    if (gameOver) {
      this.state.gameStatus = { type: 'win', winner: player };
    }

    // Record move
    const moveRecord: MoveRecord = {
      moveNumber: this.state.moveHistory.length + 1,
      player,
      diceValue: move.diceValue,
      tokenIndex: move.tokenIndex,
      action: move.action,
      fromPosition,
      toPosition: { ...this.state.tokens[player][move.tokenIndex] },
      captured,
      reachedHome,
      extraTurn: isExtraTurn && !tripleSix && !gameOver,
      tripleSix,
      timestamp: Date.now(),
    };
    this.state.moveHistory.push(moveRecord);
    this.state.lastMoveAt = Date.now();
    this.state.awaitingMove = false;

    return {
      success: true,
      move,
      newPosition: { ...this.state.tokens[player][move.tokenIndex] },
      captured,
      reachedHome,
      extraTurn: isExtraTurn && !tripleSix && !gameOver,
      tripleSix,
      turnSkipped: false,
      gameOver,
      winner: gameOver ? player : undefined,
    };
  }

  // =========================================================================
  // Turn Management
  // =========================================================================

  advanceTurn(): PlayerColor {
    if (this.state.gameStatus.type !== 'active') return this.state.currentTurn;

    const lastMove = this.state.moveHistory[this.state.moveHistory.length - 1];

    // If last move grants extra turn (rolled 6, not triple-six, game not over)
    if (lastMove && lastMove.extraTurn) {
      // Keep same player, reset dice
      this.state.lastDiceValue = null;
      this.state.awaitingMove = false;
      return this.state.currentTurn;
    }

    // Triple-six: reset consecutive sixes and advance
    if (lastMove && lastMove.tripleSix) {
      this.state.consecutiveSixes = 0;
    }

    // Advance to next active player
    const currentIndex = this.state.activePlayers.indexOf(this.state.currentTurn);
    const nextIndex = (currentIndex + 1) % this.state.activePlayers.length;
    this.state.currentTurn = this.state.activePlayers[nextIndex];
    this.state.consecutiveSixes = 0;
    this.state.lastDiceValue = null;
    this.state.awaitingMove = false;

    return this.state.currentTurn;
  }

  hasExtraTurn(): boolean {
    const lastMove = this.state.moveHistory[this.state.moveHistory.length - 1];
    if (!lastMove) return false;
    return lastMove.extraTurn;
  }

  getConsecutiveSixes(): number {
    return this.state.consecutiveSixes;
  }

  // =========================================================================
  // Token Queries
  // =========================================================================

  getTokenPositions(player: PlayerColor): TokenPosition[] {
    return this.state.tokens[player].map((t) => ({ ...t }));
  }

  getTokensInHomeBase(player: PlayerColor): number {
    return this.state.tokens[player].filter((t) => t.location === 'home_base').length;
  }

  getTokensOnBoard(player: PlayerColor): number {
    return this.state.tokens[player].filter((t) => t.location === 'track').length;
  }

  getTokensInHomeStretch(player: PlayerColor): number {
    return this.state.tokens[player].filter((t) => t.location === 'home_stretch').length;
  }

  getTokensHome(player: PlayerColor): number {
    return this.state.tokensHome[player];
  }

  // =========================================================================
  // Board Queries
  // =========================================================================

  isSafeSpot(position: number): boolean {
    return SAFE_SPOTS.includes(position);
  }

  getSquareOccupants(position: number): TokenInfo[] {
    const occupants: TokenInfo[] = [];
    for (const color of this.state.activePlayers) {
      for (const token of this.state.tokens[color]) {
        if (token.location === 'track' && token.trackPosition === position) {
          occupants.push({ player: color, tokenIndex: token.tokenIndex, position: { ...token } });
        }
      }
    }
    return occupants;
  }

  // =========================================================================
  // State Queries
  // =========================================================================

  getGameState(): LudoGameState {
    return JSON.parse(JSON.stringify(this.state));
  }

  getCurrentTurn(): PlayerColor {
    return this.state.currentTurn;
  }

  getGameStatus(): LudoGameStatus {
    return { ...this.state.gameStatus } as LudoGameStatus;
  }

  getMoveHistory(): MoveRecord[] {
    return this.state.moveHistory.map((m) => ({ ...m }));
  }

  isGameOver(): boolean {
    return this.state.gameStatus.type !== 'active';
  }

  getWinner(): PlayerColor | null {
    if (this.state.gameStatus.type === 'win') return this.state.gameStatus.winner;
    if (this.state.gameStatus.type === 'resignation') return this.state.gameStatus.winner;
    return null;
  }

  // =========================================================================
  // Serialization
  // =========================================================================

  serialize(): string {
    return JSON.stringify(this.state);
  }

  deserialize(json: string): LudoGameState | { error: string } {
    try {
      const parsed = JSON.parse(json);

      if (!parsed.tokens || typeof parsed.tokens !== 'object') {
        return { error: 'Invalid state: missing tokens' };
      }
      if (!Array.isArray(parsed.activePlayers) || parsed.activePlayers.length < 2) {
        return { error: 'Invalid state: missing or invalid activePlayers' };
      }
      if (typeof parsed.currentTurn !== 'string') {
        return { error: 'Invalid state: missing currentTurn' };
      }
      if (!Array.isArray(parsed.moveHistory)) {
        return { error: 'Invalid state: missing moveHistory' };
      }
      if (!parsed.gameStatus || typeof parsed.gameStatus.type !== 'string') {
        return { error: 'Invalid state: missing or invalid gameStatus' };
      }

      // Validate token positions
      for (const color of parsed.activePlayers) {
        if (!Array.isArray(parsed.tokens[color])) {
          return { error: `Invalid state: missing tokens for ${color}` };
        }
        if (parsed.tokens[color].length !== TOKENS_PER_PLAYER) {
          return { error: `Invalid state: ${color} must have exactly ${TOKENS_PER_PLAYER} tokens` };
        }
      }

      return parsed as LudoGameState;
    } catch (e) {
      return { error: `Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}` };
    }
  }

  setGameState(state: LudoGameState): void {
    this.state = JSON.parse(JSON.stringify(state));
  }

  // =========================================================================
  // Private Helpers
  // =========================================================================

  /**
   * Calculate where a token on the track would end up after moving diceValue steps.
   * Returns null if the move is invalid (overshoots home).
   */
  private calculateTrackMove(
    player: PlayerColor,
    currentAbsPos: number,
    diceValue: number
  ): { newLocation: TokenLocation; newTrackPos?: number; newHomeStretchPos?: number } | null {
    const startPos = PLAYER_START_POSITIONS[player];
    const entryPos = HOME_STRETCH_ENTRY[player];

    // Calculate relative position (how far from start)
    const relativePos = (currentAbsPos - startPos + TRACK_LENGTH) % TRACK_LENGTH;
    const newRelativePos = relativePos + diceValue;

    // The home stretch entry is at relative position (TRACK_LENGTH - 1) = 51
    // Actually, the entry point is the position just before entering home stretch.
    // For red: start=0, entry=50. Relative entry = 50.
    // After position 50 (relative), the token enters home stretch.
    const relativeEntry = (entryPos - startPos + TRACK_LENGTH) % TRACK_LENGTH;

    if (relativePos <= relativeEntry && newRelativePos > relativeEntry) {
      // Token is crossing into home stretch
      const stepsIntoHomeStretch = newRelativePos - relativeEntry - 1;
      if (stepsIntoHomeStretch < HOME_STRETCH_LENGTH) {
        return { newLocation: 'home_stretch', newHomeStretchPos: stepsIntoHomeStretch };
      } else if (stepsIntoHomeStretch === HOME_STRETCH_LENGTH) {
        return { newLocation: 'home' };
      } else {
        // Overshoots home — invalid
        return null;
      }
    }

    // Normal track movement
    const newAbsPos = (currentAbsPos + diceValue) % TRACK_LENGTH;
    return { newLocation: 'track', newTrackPos: newAbsPos };
  }

  /** Check if the player has their own token at a specific location */
  private isOwnTokenAt(player: PlayerColor, location: TokenLocation, position: number): boolean {
    return this.state.tokens[player].some((t) => {
      if (location === 'track') return t.location === 'track' && t.trackPosition === position;
      if (location === 'home_stretch') return t.location === 'home_stretch' && t.homeStretchPosition === position;
      return false;
    });
  }

  /** Check for and execute capture at a track position. Returns captured token info or undefined. */
  private checkAndExecuteCapture(player: PlayerColor, trackPosition: number): TokenInfo | undefined {
    // No captures on safe spots
    if (this.isSafeSpot(trackPosition)) return undefined;

    for (const color of this.state.activePlayers) {
      if (color === player) continue;
      for (let i = 0; i < this.state.tokens[color].length; i++) {
        const token = this.state.tokens[color][i];
        if (token.location === 'track' && token.trackPosition === trackPosition) {
          const capturedInfo: TokenInfo = {
            player: color,
            tokenIndex: i,
            position: { ...token },
          };
          // Send back to home base
          this.state.tokens[color][i] = {
            tokenIndex: i,
            location: 'home_base',
          };
          return capturedInfo;
        }
      }
    }
    return undefined;
  }

  private createEmptyState(): LudoGameState {
    return {
      tokens: { red: [], blue: [], green: [], yellow: [] },
      playerCount: 0,
      activePlayers: [],
      playerTypes: { red: 'human', blue: 'human', green: 'human', yellow: 'human' },
      currentTurn: 'red',
      consecutiveSixes: 0,
      lastDiceValue: null,
      awaitingMove: false,
      tokensHome: { red: 0, blue: 0, green: 0, yellow: 0 },
      moveHistory: [],
      gameStatus: { type: 'active' },
      gameId: '',
      startedAt: 0,
      lastMoveAt: 0,
    };
  }
}
